import { useState, useEffect, useRef } from 'react';
import { Search, Calendar, X, Users } from 'lucide-react';
import { FiltrosArquivo } from '../../types/gerArquivos';
import { getLastTenDaysRange } from '../../utils/date';

interface FiltrosArquivosProps {
  filtros: FiltrosArquivo;
  onFiltrosChange: (filtros: Partial<FiltrosArquivo>) => void;
  carregando?: boolean;
}

export function FiltrosArquivos({ filtros, onFiltrosChange, carregando = false }: FiltrosArquivosProps) {
  // Estado local para o input de busca (atualizado imediatamente)
  const [localSearchTerm, setLocalSearchTerm] = useState(filtros.searchTerm);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sincronizar estado local quando filtros mudarem externamente (ex: limpar filtros)
  useEffect(() => {
    setLocalSearchTerm(filtros.searchTerm);
  }, [filtros.searchTerm]);

  // Debounce: atualizar filtros após 400ms sem digitação
  useEffect(() => {
    // Limpar timer anterior se existir
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Se o valor local for diferente do filtro atual, agendar atualização
    if (localSearchTerm !== filtros.searchTerm) {
      debounceTimerRef.current = setTimeout(() => {
        onFiltrosChange({ searchTerm: localSearchTerm });
      }, 400);
    }

    // Cleanup: limpar timer ao desmontar ou quando localSearchTerm mudar
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [localSearchTerm, filtros.searchTerm, onFiltrosChange]);

  const handleSearchChange = (searchTerm: string) => {
    // Atualizar estado local imediatamente (sem debounce)
    setLocalSearchTerm(searchTerm);
  };

  const handleDateFromChange = (dateFrom: string) => {
    onFiltrosChange({ dateFrom });
  };

  const handleDateToChange = (dateTo: string) => {
    onFiltrosChange({ dateTo });
  };

  const handleShowAllUsersChange = (showAllUsers: boolean) => {
    onFiltrosChange({ showAllUsers });
  };

  const handleDateFilterEnabledChange = (enabled: boolean) => {
    onFiltrosChange({ dateFilterEnabled: enabled });
  };

  const limparFiltros = () => {
    const defaultDates = getLastTenDaysRange();
    onFiltrosChange({
      searchTerm: '',
      dateFrom: defaultDates.from,
      dateTo: defaultDates.to,
      dateFilterEnabled: true,
      showAllUsers: false
    });
  };

  const temFiltrosAtivos = localSearchTerm || filtros.dateFilterEnabled || filtros.showAllUsers;

  return (
    <div className="space-y-4">
      {/* Campo de busca */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por cliente, tipo de serviço ou arquivo..."
          value={localSearchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          disabled={carregando}
          className="w-full pl-8 pr-3 py-2 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Buscar arquivos"
        />
      </div>

      {/* Filtros de data com botão limpar integrado */}
      <div className="space-y-2">
        {/* Toggle para habilitar/desabilitar filtro de data */}
        <div className="flex items-center gap-2 p-2 bg-navy-800/50 border border-navy-600 rounded-lg">
          <input
            type="checkbox"
            id="dateFilterEnabled"
            checked={filtros.dateFilterEnabled}
            onChange={(e) => handleDateFilterEnabledChange(e.target.checked)}
            disabled={carregando}
            className="w-4 h-4 text-blue-600 bg-navy-700 border-navy-500 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Filtrar por período de datas"
          />
          <label
            htmlFor="dateFilterEnabled"
            className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer select-none"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Filtrar por período de datas</span>
          </label>
        </div>

        {/* Campos de data - desabilitados quando toggle está off */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              <Calendar className="w-3 h-3 inline mr-1" />
              Data inicial
            </label>
            <input
              type="date"
              value={filtros.dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              disabled={carregando || !filtros.dateFilterEnabled}
              className="w-full px-2 py-1.5 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Data inicial"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              <Calendar className="w-3 h-3 inline mr-1" />
              Data final
            </label>
            <input
              type="date"
              value={filtros.dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              disabled={carregando || !filtros.dateFilterEnabled}
              className="w-full px-2 py-1.5 bg-navy-800 border border-navy-600 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-400/50 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Data final"
            />
          </div>
        </div>

        {/* Switch para mostrar todos os usuários */}
        <div className="flex items-center gap-2 p-2 bg-navy-800/50 border border-navy-600 rounded-lg">
          <input
            type="checkbox"
            id="showAllUsers"
            checked={filtros.showAllUsers || false}
            onChange={(e) => handleShowAllUsersChange(e.target.checked)}
            disabled={carregando}
            className="w-4 h-4 text-blue-600 bg-navy-700 border-navy-500 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Mostrar arquivos de todos os usuários"
          />
          <label
            htmlFor="showAllUsers"
            className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer select-none disabled:opacity-50"
          >
            <Users className="w-3.5 h-3.5" />
            <span>Mostrar arquivos de todos os usuários</span>
          </label>
        </div>

        {/* Botão limpar filtros - integrado logo abaixo dos inputs */}
        {temFiltrosAtivos && (
          <button
            onClick={limparFiltros}
            disabled={carregando}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-300 hover:text-white bg-navy-700/50 hover:bg-navy-700 border border-navy-600 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Limpar filtros"
          >
            <X className="w-3.5 h-3.5" />
            <span>Limpar filtros</span>
          </button>
        )}
      </div>
    </div>
  );
}
